import { useNavigate, useParams } from "react-router-dom";
import AmountWithCurrencyInput from "components/form/amount-currency";
import { Form } from "react-crud-form";
import classNames from "classnames";
import { hourlyJobConfigSchema } from "@/modules/work/jobs/config/hourly/schema";
import { useCreateHourlyConfig, useUpdateHourlyConfig } from "@/modules/work/jobs/config/hourly/mutation";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormActions } from "components/form/actions";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "ui/components/form";
import { FormLabel } from "components/form";
import { PermissionControlledInput } from "permissions/components/input";
import { RadioGroup, RadioGroupItem } from "ui/components/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";
import { Label } from "ui/components/label";

const HourlyConfigForm = ({ config }) => {
  const navigate = useNavigate();

  const { jobId } = useParams();
  const form = useForm({
    resolver: zodResolver(hourlyJobConfigSchema),
    defaultValues: config || {
      billing_frequency: "weekly",
    },
  });
  const createMutation = useCreateHourlyConfig({});
  const updateMutation = useUpdateHourlyConfig();

  const handleCreate = async (context) => {
    const { values } = context;
    await createMutation.mutateAsync({
      jobId,
      values,
    });

    navigate(-1); // optional
  };

  const handleUpdate = async (id, context) => {
    const { dirtyValues } = context;
    await updateMutation.mutateAsync({
      id,
      values: dirtyValues,
    });
    navigate(-1);
  };

  const billingFrequency = form.watch("billing_frequency");

  const weekdays = [
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
    { label: "Sunday", value: 0 },
  ];

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      itemId={config?.id || "+"}
    >
      {({ isCreating, createItem, isUpdating, updateItem, isDirty, isNewItem }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (isNewItem) {
              createItem();
            } else {
              updateItem();
            }
          },
          { preventDefault: true },
        );
        return (
          <div className={classNames("p-6 max-w-md space-y-6")}>
            <AmountWithCurrencyInput
              form={form}
              amountKey="rate"
              amountLabel="Rate"
              collection="hourly_job_config"
              isNewItem={isNewItem}
            />

            <FormField
              control={form.control}
              name="billing_frequency"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                    collection="hourly_job_config"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={true}>Billing Frequency</FormLabel>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="weekly" id="hourly-weekly" />
                        <Label htmlFor="hourly-weekly">Weekly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monthly" id="hourly-monthly" />
                        <Label htmlFor="hourly-monthly">Monthly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yearly" id="hourly-yearly" />
                        <Label htmlFor="hourly-yearly">Yearly</Label>
                      </div>
                    </RadioGroup>
                  </PermissionControlledInput>
                  <FormDescription>
                    Choose how frequently invoices will be generated for logged hours.
                  </FormDescription>
                  <FormMessage />

                </FormItem>
              )}
            />

            {billingFrequency === "weekly" && (
              <FormField
                control={form.control}
                name="billing_by_week_day"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="hourly_job_config"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={true}>Billing by Weekday</FormLabel>
                      <Select
                        onValueChange={(val) => field.onChange(Number(val))}
                        value={field.value?.toString()}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a weekday" />
                        </SelectTrigger>
                        <SelectContent>
                          {weekdays.map((day) => (
                            <SelectItem
                              key={day.value}
                              value={day.value.toString()}
                            >
                              {day.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </PermissionControlledInput>
                    <FormDescription>
                      Select the day of the week when weekly billing cycle ends and generates invoice.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {billingFrequency === "monthly" && (
              <FormField
                control={form.control}
                name="billing_by_month_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="hourly_job_config"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={true}>Billing by Month Date (1-31)</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(Number(value))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a date" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (date) => (
                              <SelectItem key={date} value={date.toString()}>
                                {date}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </PermissionControlledInput>
                    <FormDescription>
                      Select the date of the month when monthly billing cycle ends and generates invoice.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {billingFrequency === "yearly" && (
              <FormField
                control={form.control}
                name="billing_by_month"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="hourly_job_config"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={true}>Billing by Month</FormLabel>
                      <Select
                        onValueChange={(val) => field.onChange(Number(val))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a month" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {months.map((month, index) => (
                            <SelectItem key={month} value={index.toString()}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </PermissionControlledInput>
                    <FormDescription>
                      Select the month of the year when annual billing cycle ends and generates invoice.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormActions
              rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
              collection="hourly_job_config"
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              createItem={createItem}
              updateItem={updateItem}
              isDirty={isDirty}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default HourlyConfigForm;
